"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuditService = void 0;
const symbol_sdk_1 = require("symbol-sdk");
const model_1 = require("../model");
const hash_1 = require("../utils/hash");
class AuditService {
    constructor(data, txHash, apiEndpoint) {
        this.data = data;
        this.txHash = txHash;
        this.repositoryFactory = new symbol_sdk_1.RepositoryFactoryHttp(apiEndpoint);
    }
    static audit(data, txHash, apiEndpoint) {
        return __awaiter(this, void 0, void 0, function* () {
            const transactionStatusHttp = new symbol_sdk_1.TransactionStatusHttp(apiEndpoint);
            const status = yield transactionStatusHttp.getTransactionStatus(txHash).toPromise();
            if (status.group === 'confirmed') {
                const result = yield this.auditWithComplete(data, txHash, apiEndpoint);
                return result;
            }
            if (status.group === 'partial') {
                const result = yield this.auditWithPartial(data, txHash, apiEndpoint);
                return result;
            }
            throw Error('transaction not found');
        });
    }
    static auditWithComplete(data, txHash, apiEndpoint) {
        return __awaiter(this, void 0, void 0, function* () {
            const service = new AuditService(data, txHash, apiEndpoint);
            const result = yield service.auditWithConfirmed();
            return result;
        });
    }
    static auditWithPartial(data, txHash, apiEndpoint) {
        return __awaiter(this, void 0, void 0, function* () {
            const service = new AuditService(data, txHash, apiEndpoint);
            const result = yield service.auditWithPartial();
            return result;
        });
    }
    auditWithConfirmed() {
        return __awaiter(this, void 0, void 0, function* () {
            const coreTx = yield this.getTransaction();
            const singerPublicAccount = coreTx.signer;
            const parsedMessage = this.parseMessage(coreTx.message.payload);
            const hashFunction = hash_1.HashFunctionCreator.createWithTypeStr(parsedMessage.hashingTypeStr);
            const hashedData = hashFunction.hashing(this.data);
            const isValid = singerPublicAccount.verifySignature(hashedData, parsedMessage.signedHash);
            if (isValid) {
                const signer = coreTx.signer.address;
                const apostilleAccount = coreTx.recipientAddress;
                const timestamp = yield this.getTimestamp(coreTx.transactionInfo.height);
                const result = {
                    isValid: true,
                    type: model_1.AuditType.Confirmed,
                    signer,
                    apostilleAccount,
                    timestamp
                };
                return result;
            }
            const result = { isValid: false };
            return result;
        });
    }
    auditWithPartial() {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = yield this.getTransactionFromPartial();
            const coreTx = this.getCoreTx(tx);
            const signerPublicAccount = coreTx.signer;
            const parsedMessage = this.parseMessage(coreTx.message.payload);
            const hashFunction = hash_1.HashFunctionCreator.createWithTypeStr(parsedMessage.hashingTypeStr);
            const hashedData = hashFunction.hashing(this.data);
            const isValid = signerPublicAccount.verifySignature(hashedData, parsedMessage.signedHash);
            if (isValid) {
                const signer = coreTx.signer.address;
                const apostilleAccount = coreTx.recipientAddress;
                const multisigTx = this.getMultisigTx(tx);
                const additions = multisigTx.addressAdditions;
                const cosignatories = this.getCosignatories(tx);
                const result = {
                    isValid: true,
                    type: model_1.AuditType.Partial,
                    signer,
                    apostilleAccount,
                    multisigAdditional: additions,
                    cosignatories
                };
                return result;
            }
            const result = {
                isValid: false,
            };
            return result;
        });
    }
    getTimestamp(blockHight) {
        return __awaiter(this, void 0, void 0, function* () {
            const blockRep = this.repositoryFactory.createBlockRepository();
            const blockInfo = yield blockRep.getBlockByHeight(blockHight).toPromise();
            const t = Number(blockInfo.timestamp.toString());
            const epochAdjustment = yield this.getEpochAdjustment();
            return new Date(t + epochAdjustment * 1000);
        });
    }
    getEpochAdjustment() {
        return __awaiter(this, void 0, void 0, function* () {
            const epochAdjustment = yield this.repositoryFactory.getEpochAdjustment().toPromise();
            return epochAdjustment;
        });
    }
    parseMessage(txMessage) {
        const regex = /^fe4e5459(\d{2})(\w+)/;
        const result = txMessage.match(regex);
        if (result) {
            const parsedMessage = {
                hashingTypeStr: result[1],
                signedHash: result[2],
            };
            return parsedMessage;
        }
        throw Error('It is not apostille message');
    }
    getTransaction() {
        return __awaiter(this, void 0, void 0, function* () {
            const transactionRep = this.repositoryFactory.createTransactionRepository();
            const tx = yield transactionRep.getTransaction(this.txHash, symbol_sdk_1.TransactionGroup.Confirmed).toPromise();
            if (tx instanceof symbol_sdk_1.AggregateTransaction) {
                const coreInnerTx = tx.innerTransactions.find(t => t instanceof symbol_sdk_1.TransferTransaction && t.message.payload.startsWith('fe4e5459'));
                if (coreInnerTx) {
                    return coreInnerTx;
                }
                throw Error('It is not apostille transaction');
            }
            else {
                throw Error('It is not apostille transaction');
            }
        });
    }
    getTransactionFromPartial() {
        return __awaiter(this, void 0, void 0, function* () {
            const transactionRep = this.repositoryFactory.createTransactionRepository();
            const tx = yield transactionRep.getTransaction(this.txHash, symbol_sdk_1.TransactionGroup.Partial).toPromise();
            if (tx instanceof symbol_sdk_1.AggregateTransaction) {
                const coreInnerTx = tx.innerTransactions.find(t => t instanceof symbol_sdk_1.TransferTransaction && t.message.payload.startsWith('fe4e5459'));
                if (coreInnerTx) {
                    return tx;
                }
                throw Error('It is not apostille transaction');
            }
            else {
                throw Error('It is not apostille transaction');
            }
        });
    }
    getCoreTx(tx) {
        const coreTx = tx.innerTransactions.find(t => t instanceof symbol_sdk_1.TransferTransaction && t.message.payload.startsWith('fe4e5459'));
        if (coreTx) {
            return coreTx;
        }
        throw Error('It is not apostille transaction');
    }
    getMultisigTx(tx) {
        const multisigTx = tx.innerTransactions.find(t => t instanceof symbol_sdk_1.MultisigAccountModificationTransaction);
        if (multisigTx) {
            return multisigTx;
        }
        throw Error('It is not contain multisig tx');
    }
    getCosignatories(tx) {
        const cosignatories = [];
        tx.cosignatures.forEach((c) => {
            cosignatories.push(c.signer.address);
        });
        return cosignatories;
    }
}
exports.AuditService = AuditService;
//# sourceMappingURL=AuditService.js.map